import React from "react";

import campaignStepOneIcon from "../../assets/images/svg/mab-compgn-1.svg";
import dropOpenIcon from "../../assets/images/svg/mab-drop-open-1.svg";
import downArrow from "../../assets/images/svg/mab-down-arrow.svg";
import ellipseBg from "../../assets/images/svg/mab-ellipse.svg";
import NextButton from "../common/next-button";

type MusicDropdownProps = {
  id: string;
  expandedId: string | null;
  onToggle: (id: string) => void;
  onNext?: (id: string) => void;
  promoteType?: "track" | "playlist";
  spotifyUrl: string;
  artistName: string;
  title: string;
  showAdditionalFields: boolean;
  onSpotifyUrlChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onArtistNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onTitleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isEditMode?: boolean;
};

const MusicDropdown: React.FC<MusicDropdownProps> = ({
  id,
  expandedId,
  onToggle,
  onNext,
  promoteType = "track",
  spotifyUrl,
  artistName,
  title,
  showAdditionalFields,
  onSpotifyUrlChange,
  onArtistNameChange,
  onTitleChange,
  isEditMode = false,
}) => {
  const isOpen = expandedId === id;

  return (
    <div className="w-full">
      <div
        className="relative rounded-[12px] sm:rounded-[16px] md:rounded-[20px] shadow-[0_14px_40px_rgba(0,0,0,0.25)] overflow-hidden w-full"
        style={{
          background: "#878DA11A",
          backgroundImage: `url(${ellipseBg})`,
          backgroundSize: "100% 100%",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div
          className="flex items-center gap-2 sm:gap-3 cursor-pointer hover:opacity-90 transition-opacity min-h-[60px] h-[60px] sm:min-h-[72px] sm:h-[72px] md:min-h-[88px] md:h-[88px] px-4 py-4 sm:px-4 sm:py-4 md:px-6 md:py-6"
          onClick={() => onToggle(id)}
        >
          <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
            <img
              src={isOpen ? dropOpenIcon : campaignStepOneIcon}
              alt="Step 1"
              className="w-7 h-7 sm:w-8 sm:h-8 flex-shrink-0"
            />
            <span
              className="text-[16px] whitespace-nowrap"
              style={{
                fontFamily: "Poppins",
                fontWeight: 400,
                fontStyle: "normal",
                lineHeight: "130%",
                letterSpacing: "0%",
                textAlign: "center",
                color: isOpen ? "#58F2A9" : "#FFFFFF",
              }}
            >
              Music
            </span>
          </div>
          <img
            src={downArrow}
            alt="Toggle"
            className="pointer-events-none transition-transform flex-shrink-0"
            style={{
              height: "9.5px",
              width: "auto",
              transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
              filter: isOpen
                ? "brightness(0) saturate(100%) invert(78%) sepia(89%) saturate(1352%) hue-rotate(90deg) brightness(102%) contrast(101%)"
                : "none",
            }}
          />
        </div>

        {isOpen && (
          <div className="flex flex-col gap-3 md:gap-6 px-4  md:px-6 md:pb-6 items-start w-full">
            <div className="w-full flex flex-col gap-[16px]">
              <label
                className="text-left text-[14px] lg:text-[16px]"
                style={{
                  color: "#FFFFFF",
                  fontWeight: 400,
                  fontStyle: "normal",
                  lineHeight: "130%",
                  letterSpacing: "0%",
                }}
              >
                {promoteType === "playlist"
                  ? "Add your Spotify playlist URL"
                  : "Add your Spotify song URL"}
              </label>
              <input
                type="text"
                placeholder={
                  promoteType === "playlist"
                    ? "https://open.spotify.com/playlist/...."
                    : "https://open.spotify.com/track/...."
                }
                value={spotifyUrl}
                onChange={onSpotifyUrlChange}
                disabled={isEditMode}
                className="bg-white rounded-[12px] focus:outline-none focus:ring-2 focus:ring-[#58F2A9] 
             w-full px-4 py-2 h-[48px] lg:h-[55px]"
                style={{
                  paddingTop: "6px",
                  paddingRight: "16px",
                  paddingBottom: "6px",
                  paddingLeft: "16px",
                  borderRadius: "12px",
                  opacity: isEditMode ? 0.6 : 1,
                  maxWidth: "100%",
                  fontSize: "0.8rem",
                  backgroundColor: isEditMode ? "#f0f0f0" : "white",
                  cursor: isEditMode ? "not-allowed" : "text",
                }}
              />
            </div>

            {showAdditionalFields && promoteType !== "playlist" && (
              <div className="w-full flex flex-col lg:flex-row lg:gap-4 gap-3">
                <div className="w-full lg:flex-1 flex flex-col gap-2 min-w-0">
                  <label
                    className="text-left text-[14px] lg:text-[16px]"
                    style={{
                      color: "#FFFFFF",
                      fontWeight: 400,
                      fontStyle: "normal",
                      lineHeight: "130%",
                      letterSpacing: "0%",
                    }}
                  >
                    Enter artist name
                  </label>
                  <input
                    type="text"
                    placeholder="Artist name"
                    value={artistName}
                    onChange={onArtistNameChange}
                    className="bg-white rounded-[12px] focus:outline-none focus:ring-2 focus:ring-[#58F2A9] w-full px-2 py-2  h-[48px] lg:h-[55px]"
                    style={{
                      paddingTop: "6px",
                      paddingRight: "16px",
                      paddingBottom: "6px",
                      paddingLeft: "16px",
                      borderRadius: "12px",
                      opacity: 1,
                      maxWidth: "100%",
                      boxSizing: "border-box",
                      fontSize: "0.8rem",
                    }}
                  />
                </div>

                <div className="w-full lg:flex-1 flex flex-col gap-2 min-w-0">
                  <label
                    className="text-left text-[14px] lg:text-[16px]"
                    style={{
                      color: "#FFFFFF",
                      fontWeight: 400,
                      fontStyle: "normal",
                      lineHeight: "130%",
                      letterSpacing: "0%",
                    }}
                  >
                    Enter title
                  </label>
                  <input
                    type="text"
                    placeholder="Song title"
                    value={title}
                    onChange={onTitleChange}
                    className="bg-white rounded-[12px] focus:outline-none focus:ring-2 focus:ring-[#58F2A9] w-full px-2 py-2  h-[48px] lg:h-[55px]"
                    style={{
                      paddingTop: "6px",
                      paddingRight: "16px",
                      paddingBottom: "6px",
                      paddingLeft: "16px",
                      borderRadius: "12px",
                      opacity: 1,
                      maxWidth: "100%",
                      boxSizing: "border-box",
                      fontSize: "0.8rem",
                    }}
                  />
                </div>
              </div>
            )}

            <div className="flex justify-end w-full">
              <NextButton
                size="md"
                className="w-full md:w-auto"
                onClick={() => onNext && onNext(id)}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MusicDropdown;
